import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useChatStore } from '@/stores/chatStore';
import { useAuthStore } from '@/stores/authStore';
import { useActiveCell } from '@/hooks/useActiveCell';
import { captureWorkbookSnapshotCached, isCellEditingModeError, isCellTypingMode, type CapturedImage } from '@/services/office';
import { streamChat, type ChatStreamController } from '@/services/chat';
import { listModels } from '@/services/models';
import { generateSuggestions } from '@/services/suggestions';
import { extractErrorMessage } from '@/services/api';
import type { UsagePayload, WorkbookContext } from '@/types/chat';
import type { ModelsResponse } from '@/types/api';
import { MessageList } from './MessageList';
import { InputBox, type InputBoxHandle } from './InputBox';
import { PendingDiffBar } from './PendingDiffBar';
import type { Template } from './Templates';

export function ChatInterface() {
  const {
    provider, model, conversationId,
    streaming, appendUserMessage, startAssistantStream,
    appendAssistantDelta, attachToolCall, attachToolResult,
    attachDiffDelta, attachDiff, attachClarify, resolveClarify,
    finalizeAssistant, failAssistant,
    setThinking, setPhase, setConversation,
    setSuggestions,
  } = useChatStore();

  // phaseFromTool maps a tool's facade name to the loading-animation
  // phase. Read tools → "reading"; cell/sheet/format/etc writes →
  // "writing"; charts and pivots → "creating"; web tools → "searching";
  // analysis + stats → "computing". Anything else (ask_clarification,
  // unknown) falls back to thinking. Single map, no provider-specific
  // branches — kept here so the SSE handler stays small.
  const phaseFromTool = (toolName: string): import('@/stores/chatStore').LoadingPhase => {
    switch (toolName) {
      case 'read_workbook':
        return 'reading';
      case 'cell_op':
      case 'structure_op':
      case 'sheet_op':
      case 'format_op':
      case 'data_op':
      case 'annotate_op':
        return 'writing';
      case 'visual_op':
        return 'creating';
      case 'web_op':
        return 'searching';
      case 'analysis_op':
      case 'compute_stats':
        return 'computing';
      default:
        return 'thinking';
    }
  };

  const activeContext = useActiveCell();
  const controllerRef = useRef<ChatStreamController | null>(null);
  const lastUsageRef = useRef<UsagePayload | undefined>(undefined);
  const inputRef = useRef<InputBoxHandle | null>(null);
  const [modelsData, setModelsData] = useState<ModelsResponse | null>(null);

  // Load the model catalog once so we can answer "does current model
  // support vision?" without pinging the server per keystroke.
  useEffect(() => {
    listModels().then(setModelsData).catch(() => setModelsData(null));
  }, []);

  const visionSupported = useMemo(() => {
    if (!modelsData) return false;
    const list = modelsData.models?.[provider] ?? [];
    return list.find((m) => m.id === model)?.supports_vision ?? false;
  }, [modelsData, provider, model]);

  const onTemplatePick = (t: Template) => {
    inputRef.current?.setText(t.prompt);
  };

  const send = async (msg: string, images?: CapturedImage[], attachmentIDs?: string[]) => {
    appendUserMessage(msg);
    startAssistantStream();
    // Animation-only loading. The "Reading your workbook…" string is
    // retained for screen readers / telemetry but the visible UI shows
    // the LoadingPulse animation in the matching phase. Default phase
    // for the snapshot-capture window is "reading" since that's
    // literally what's happening on the client.
    setThinking('Reading your workbook…');
    setPhase('reading');
    lastUsageRef.current = undefined;

    // Capture the full workbook snapshot once per turn — richer than
    // the active-cell poll so tools have something to inspect.
    //
    // captureWorkbookSnapshotCached memoises within a 5-second TTL +
    // hash check, so rapid follow-ups ("ab bold kar", "title bada karo")
    // skip the 100-300ms Excel.run() walk entirely. The cached path is
    // automatically invalidated on selection-change events wired in the
    // useActiveContext hook.
    //
    // If the user is mid-edit in a cell, Office.js rejects every API
    // call with "cell-editing mode" — we detect that up front and abort
    // with a friendly message rather than letting the backend hallucinate
    // against an empty snapshot.
    let snapshot: WorkbookContext = {};
    let workbookHash = '';
    try {
      const cap = await captureWorkbookSnapshotCached();
      snapshot = cap.snapshot;
      workbookHash = cap.hash;
    } catch (err) {
      if (isCellEditingModeError(err)) {
        // Cell-typing mode = user mid-edit inside a cell; no API exists
        // to exit it programmatically. Chart/object selected case is
        // already auto-recovered inside captureWorkbookSnapshotCached,
        // so reaching here for a non-typing error means recovery failed.
        if (isCellTypingMode(err)) {
          failAssistant('Cell edit mode active hai — Enter ya Escape press karke exit karo, phir query bhejo.');
          toast.error('Cell mein type ho raha hai — Enter/Escape press karo.');
        } else {
          failAssistant('Workbook capture nahi ho paaya — kisi empty cell pe click karke dobara bhejo.');
          toast.error('Selection issue — empty cell pe click karke retry karo.');
        }
        return;
      }
      // Other capture failures: continue with empty snapshot; agent will
      // call read_workbook tools if it needs data.
      console.warn('workbook capture failed:', err);
    }
    // Merge: full snapshot may miss the *latest* selection if the user
    // moved the cursor between captures; overlay the active context.
    const workbookContext = { ...snapshot, ...activeContext };

    controllerRef.current = streamChat(
      {
        message: msg,
        conversation_id: conversationId ?? undefined,
        workbook_hash: workbookHash || undefined,
        workbook_context: workbookContext,
        images: images?.map((i) => ({ media_type: i.mediaType, data: i.data })),
        attachment_ids: attachmentIDs,
      },
      {
        onThinking: (p) => {
          // Backend "thinking" events arrive between iterations as the
          // model deliberates. We KEEP these for telemetry (thinkingText)
          // but route the visible UI through loadingPhase so the
          // animation reflects the agent's stage, not a free-text label.
          setThinking(p.message);
          setPhase('thinking');
        },
        // Reasoning deltas come from the model's extended-thinking
        // stream AND from the agent's internal "self-correcting…",
        // "verifying proposal…" status updates. Animation-only path:
        // the raw text never reaches the DOM — phase stays whatever
        // the most recent tool_call (or thinking event) set it to.
        onReasoning: (p) => {
          if (p.text && p.text.trim() !== '') {
            setThinking(p.text.trim());
          }
        },
        onText: (p) => {
          // Final user-facing text starts streaming → kill the
          // animation; the message bubble itself replaces it.
          setThinking(null);
          setPhase(null);
          appendAssistantDelta(p.chunk);
        },
        onToolCall: (p) => {
          attachToolCall(p);
          // Tool cards render below; show the matching animation
          // while the tool runs server- or client-side.
          setPhase(phaseFromTool(p.name));
          setThinking(p.name);
        },
        onToolResult: (p) => {
          attachToolResult(p);
          // Tool finished — animation defaults back to thinking
          // until the next tool_call or final text arrives.
          setPhase('thinking');
        },
        onDiffDelta: (p) => attachDiffDelta(p),
        onDiffReady: (p) => attachDiff(p),
        onClarify: (p) => {
          attachClarify(p);
          setThinking(null);
          setPhase(null);
        },
        onUsage: (p) => {
          lastUsageRef.current = p;
          if (p.credits_remaining !== undefined) {
            useAuthStore.getState().updateCredits(p.credits_remaining);
          }
        },
        onDone: (p) => {
          finalizeAssistant(lastUsageRef.current, p.message_id);
          lastUsageRef.current = undefined;
          setConversation(p.conversation_id);
          // Fire suggestions asynchronously. We read the assistant text
          // from the latest store snapshot because finalizeAssistant has
          // just merged the streamed chunks into it. Errors / empty
          // arrays just mean no chips — never toast.
          const finalId = p.message_id;
          if (finalId) {
            const snapshot = useChatStore.getState().messages;
            const assistant = snapshot.find((x) => x.id === finalId);
            const userIdx = snapshot.findIndex((x) => x.id === finalId) - 1;
            const user = userIdx >= 0 ? snapshot[userIdx] : undefined;
            if (assistant?.content && user?.content) {
              generateSuggestions({
                provider,
                last_user_message: user.content,
                last_assistant_msg: assistant.content,
              })
                .then((items) => {
                  if (items.length > 0) setSuggestions(finalId, items);
                })
                .catch(() => {
                  /* silent — chips are a polish feature */
                });
            }
          }
        },
        onError: (p) => {
          failAssistant(p.message || 'Chat failed');
          toast.error(p.message ?? 'Chat failed');
        },
      },
    );
  };

  const cancel = () => {
    controllerRef.current?.abort();
    failAssistant('Cancelled by user');
  };

  const { sessionTokens } = useChatStore();
  const userCredits = useAuthStore((s) => s.user?.credits);

  return (
    <div className="flex h-full flex-col">
      {/* Session status strip — visible while streaming or after at
          least one turn. Tokens only — no USD cost (product does not
          surface pricing to users). */}
      {(sessionTokens > 0 || streaming || userCredits !== undefined) && (
        <div className="flex items-center gap-3 border-b border-border bg-surface px-2 py-1 text-[10px] text-text-secondary dark:border-border-dark dark:bg-surface-dark">
          {streaming && (
            <span className="inline-flex items-center gap-1">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary" />
              working…
            </span>
          )}
          {userCredits !== undefined && (
            <span
              className={
                userCredits <= 0
                  ? 'text-red-500 font-medium'
                  : userCredits <= 1000
                  ? 'text-amber-500'
                  : ''
              }
              title={
                userCredits <= 0
                  ? 'Credits khatam — admin se contact karo'
                  : userCredits <= 1000
                  ? 'Credits kam hain'
                  : 'Remaining credits'
              }
            >
              {userCredits.toLocaleString()} credits
            </span>
          )}
          {sessionTokens > 0 && (
            <span
              className={
                sessionTokens >= 80000
                  ? 'text-red-500 font-medium'
                  : sessionTokens >= 50000
                  ? 'text-amber-500'
                  : ''
              }
              title={
                sessionTokens >= 80000
                  ? 'Long session — consider starting a new chat to keep responses fast.'
                  : sessionTokens >= 50000
                  ? 'Conversation is getting long; older context may be summarised.'
                  : 'Total tokens used this session'
              }
            >
              {sessionTokens >= 1000
                ? `${(sessionTokens / 1000).toFixed(1)}K context`
                : `${sessionTokens} context`}
            </span>
          )}
        </div>
      )}

      <MessageList
        onClarifyPick={(messageId, interpretation) => {
          resolveClarify(messageId, interpretation);
          send(interpretation);
        }}
        onTemplatePick={onTemplatePick}
        onSuggestionPick={(text) => inputRef.current?.setText(text)}
      />

      {/* Sticky action bar — Apply / Reject sit just above the input
          box whenever there's a pending diff. Inline DiffPreview still
          renders inside the message stream for review (checkboxes,
          before/after); this bar guarantees the action buttons are
          always visible without scrolling, even on long answers. */}
      <PendingDiffBar conversationId={conversationId} />

      <InputBox
        ref={inputRef}
        onSend={(m, images, attachmentIDs) => {
          try {
            send(m, images, attachmentIDs);
          } catch (e) {
            toast.error(extractErrorMessage(e, 'Could not start chat'));
          }
        }}
        onCancel={cancel}
        streaming={streaming}
        workbookContext={activeContext}
        visionSupported={visionSupported}
      />
    </div>
  );
}
